/**
 * Dashboard v2 — Lifecycle Command Center design tokens (V1, shared-first).
 *
 * The canonical values live in `index.css` as `--dv-*` CSS custom properties,
 * split by theme (`:root`/`[data-theme="light"]` and `[data-theme="dark"]`) and
 * verified against WCAG 2.1 AA (see `.claude/memory/epic_lifecycle_dashboard.md`).
 *
 * CSS consumers should reference `var(--dv-…)` directly. This module is for the
 * JS consumers that can't — chiefly Recharts, whose SVG marks, `ReferenceArea`
 * bands, gradients and axis strokes frequently need a resolved color string
 * rather than a `var()` reference. `useDashboardTokens()` reads the *computed*
 * values off the document root so JS and CSS never drift, and re-resolves when
 * the theme flips.
 */
import { useEffect, useState } from 'react'
import { useTheme } from '../context/ThemeContext'

/** Every `--dv-*` token, keyed by a camelCase name → its CSS custom property. */
export const DV_TOKENS = {
  bg: '--dv-bg',
  card: '--dv-card',
  card2: '--dv-card-2',
  elevated: '--dv-elevated',
  line: '--dv-line',

  text: '--dv-text',
  slate: '--dv-slate',
  muted: '--dv-muted',

  // Categorical chart marks
  added: '--dv-cat-added',
  changed: '--dv-cat-changed',
  cred: '--dv-cat-cred',
  removed: '--dv-cat-removed',
  amber: '--dv-cat-amber',

  // Soft tag backgrounds + accessible on-color text
  addedSoft: '--dv-added-soft',
  addedOn: '--dv-added-on',
  changedSoft: '--dv-changed-soft',
  changedOn: '--dv-changed-on',
  credSoft: '--dv-cred-soft',
  credOn: '--dv-cred-on',
  removedSoft: '--dv-removed-soft',
  removedOn: '--dv-removed-on',
  amberSoft: '--dv-amber-soft',
  amberOn: '--dv-amber-on',

  // Trend (health-direction)
  up: '--dv-up',
  upBg: '--dv-up-bg',
  down: '--dv-down',
  downBg: '--dv-down-bg',

  ringTrack: '--dv-ring-track',
  axis: '--dv-axis',
  focus: '--dv-focus',
} as const

export type DashboardTokenName = keyof typeof DV_TOKENS
export type DashboardTokens = Record<DashboardTokenName, string>

/**
 * The four lifecycle change categories, in stack/legend order, with a stable
 * key, a human label, and the token holding their mark color. Non-color cues
 * (labels, diverging position) are the responsibility of the chart (V5); this
 * is the single source for category order + naming so the legend, KPI band,
 * feed tags and chart agree.
 */
export const CHANGE_CATEGORIES = [
  { key: 'added', label: 'Added', token: 'added' },
  { key: 'changed', label: 'Changed', token: 'changed' },
  { key: 'cred', label: 'Credential', token: 'cred' },
  { key: 'removed', label: 'Removed', token: 'removed' },
] as const satisfies ReadonlyArray<{
  key: string
  label: string
  token: DashboardTokenName
}>

/** Resolve every `--dv-*` token to its computed value off the document root. */
export function readDashboardTokens(): DashboardTokens {
  const style = getComputedStyle(document.documentElement)
  const out = {} as DashboardTokens
  for (const name in DV_TOKENS) {
    const key = name as DashboardTokenName
    out[key] = style.getPropertyValue(DV_TOKENS[key]).trim()
  }
  return out
}

/**
 * Theme-aware hook returning resolved `--dv-*` values for JS consumers.
 * Re-resolves whenever the theme mode changes so Recharts marks track the
 * light/dark twin without hard-coding either palette.
 */
export function useDashboardTokens(): DashboardTokens {
  const { themeMode } = useTheme()
  const [tokens, setTokens] = useState<DashboardTokens>(() =>
    typeof document !== 'undefined'
      ? readDashboardTokens()
      : ({} as DashboardTokens),
  )

  useEffect(() => {
    // The `data-theme` attribute is set synchronously by ThemeProvider's effect,
    // but we resolve on the next frame to be certain the new values have applied.
    const id = requestAnimationFrame(() => setTokens(readDashboardTokens()))
    return () => cancelAnimationFrame(id)
  }, [themeMode])

  return tokens
}
